import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type { {[key: string]: string } } */
  #requests = {};

  #isGalleryLoading = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    // What are we missing?
    // - need to support swatches potentially, we will need to check on what's needed for that.

    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    this.isOnPage ||= {}
    this.isOnPage.product =
      Theme.template.name.split('.').find(f => f === 'product') &&
      !event.target.closest('product-card') &&
      !event.target.closest('product-card-add-on') &&
      !event.target.closest('quick-add-dialog');

    this.isOnPage.landing = event.target.closest('.sn_product_show');

    //

    // The following line activates the loader also on `product-show` component
    // this.productGallery = (this.isOnPage.product || this.isOnPage.landing) &&
    this.productGallery = (this.isOnPage.product) &&
      !event.target.closest('product-card') &&
      !event.target.closest('product-card-add-on') ?
      document.querySelector('.sn_main_product_gallery, .sn_product_show__visual__wp') :
      null;

    this.productGalleryCard = this.productGallery?.querySelector('.sn_main_product_gallery__card');

    this.productGalleryModal = this.isOnPage.product &&
      !event.target.closest('product-card') &&
      !event.target.closest('product-card-add-on') ?
      document.querySelector('.sn_modal_product_gallery') :
      null;

    // console.log('this.productGallery', this.productGallery);

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = this.isOnPage.product && !!newUrl && newUrl !== currentUrl;

    // Fetch the updated section, but if we are in the "Configurator PDP", we do
    // it after showing the loader, so the user has a loading "effect" immediately
    // after the click
    this.fetchUpdatedSection(this.buildRequestUrl(event.target, loadsNewProduct), loadsNewProduct);

    const url = new URL(window.location.href);

    if (this.isOnPage.product) {
      if (event.target.dataset.variantId) {
        url.searchParams.set('variant', event.target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, shouldMorphMain = false, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL

    if(this.isOnPage.landing) {
      return `${productUrl}?section_id=product-show&${params.join('&')}`;
    };

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }

      // Differenciate the card to render, because the "recommended" versions
      // have a different "quick-add" button, and we need a different markup
      // in order to render the card correctly ¯\_(ツ)_/¯
      const section = this.closest('product-card')?.classList.contains('__recommended_small') ? (
        'section-rendering-product-card-recommended'
      ) : this.closest('product-card-add-on') ? (
        'section-rendering-product-card-add-on'
      ) : (
        'section-rendering-product-card'
      );

      return `${productUrl}?section_id=${section}&${params.join('&')}`;
    }

    if (shouldMorphMain) {
      return `${productUrl}?${params.join('&')}`;
    } else {
      const section = selectedOption.closest('.shopify-section').id.replace('shopify-section-', '');
      return `${productUrl}?section_id=${section}&${params.join('&')}`;
    }
  }

  autoSelectSingleOptionAfterMorph() {
    const fieldsets = this.querySelectorAll('fieldset');

    fieldsets.forEach(fieldset => {
      if (fieldset.dataset.primaryType == JSON.stringify(true)) return;

      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const validRadios = radios.filter(r => r.dataset.variantId);

      if (validRadios.length === 1) {
        const radio = validRadios[0];
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', {
            bubbles: true,
            cancelable: true
          }));
        }
        fieldset.classList.add('is-single-option');
      }
    });
  }

  get isSelectionComplete() {
    const checkedRadios = Array.from(
      this.querySelectorAll('fieldset input[type="radio"]:checked')
    );

    return checkedRadios.every(radio => radio.dataset.variantId);
  }
  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    let fetched = false;
    const colors = ['gold'];

    const fetchRequest = () => {
      fetched = true;
      console.tagLogDebug('VariantPicker - FETCH', `Fetching variant for url "${requestUrl}"`, ...colors);
      return fetch(requestUrl, { signal: this.#abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          this.#requests[requestUrl] = responseText;
          return responseText
        })
    }

    const request = async () => {

      if (this.#requests[requestUrl]) {
        fetched = false;
        console.tagLogDebug('VariantPicker - SKIP', `Skipping fetch for url "${requestUrl}"`, ...colors);
        return new Promise(resolve => resolve(this.#requests[requestUrl]));
      }

      if (this.productGallery && !this.#isGalleryLoading) {
        this.#isGalleryLoading = true;

        return window
          .loaderShow(this.productGalleryCard || this.productGallery, { flat: false })
          .then(fetchRequest.bind(this));
      }

      return fetchRequest();
    }

    request().then((responseText) => {
      this.#pendingRequestUrl = undefined;
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      // Defer is only useful for the initial rendering of the page. Remove it here.
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

      //Only used for section product-show
      const textContentVariantObj = html.querySelector(`[data-current-variant-obj]`)?.textContent;

      const textContentVP = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
      const textContentSVPC = html.querySelector(`swatches-variant-picker-component script[type="application/json"]`)?.textContent;

      const textContent = textContentVariantObj || textContentVP || textContentSVPC || '';

      if (!textContent) return;

      if (shouldMorphMain) {
        this.updateMain(html);
      } else {
        let newProduct = null;

        if (!textContentVariantObj) {
          newProduct = this.updateVariantPicker(html);

          requestAnimationFrame(() => {
            this.autoSelectSingleOptionAfterMorph();
          });

          if (this.isSelectionComplete) {
            this.updateProductGallery(html);
            this.updateDetailsInfo(html);
            this.updateShippingInfo(html);
            this.updateAddOnVariants(html);
          }
        } else {
          window.requestAnimationFrame(() => {
            window.loaderHide(this.productGallery?.querySelectorAll('.sn_loader__wr'));
            this.#isGalleryLoading = false;
          });
        }

        // We grab the variant object from the response and dispatch an event with it.
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            }, fetched)
          );
        }
      }
    })
    .catch((error) => {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted by user');
      } else {
        console.error(error);
      }
    });

  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    // If we are in the "PDP landing", the variant picker is automatically updated
    // with Alpine, so there's no need to update it here, or throw some useless errors
    if (this.isOnPage.landing) return;

    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the product gallery, if present
   * @param {Document} newHtml - The new HTML.
   */
  updateProductGallery(newHtml) {
    // If there's no current gallery, it means we are not in the product page,
    // so return immediately

    if(!this.productGallery) return;

    // If we are in the product page, get the new gallery, and:
    const galleryWrapper = this.productGallery.querySelector('.sn_main_product_gallery__images .swiper-wrapper');
    const thumbsWrapper = this.productGallery.querySelector('.sn_main_product_gallery__thumbs .swiper-wrapper');
    const newGalleryWrapper = newHtml.querySelector('.sn_main_product_gallery__images .swiper-wrapper');
    const newThumbsWrapper = newHtml.querySelector('.sn_main_product_gallery__thumbs .swiper-wrapper');
    // console.log({ gallery: this.productGallery, newGallery });

    const show = () => {
      window.requestAnimationFrame(() => {
        window.loaderHide(this.productGallery.querySelectorAll('.sn_loader__wr'));
        this.#isGalleryLoading = false;
      });
    }

    // If there's a new gallery, show the parent column and morph the content.
    morph(galleryWrapper, newGalleryWrapper);
    morph(thumbsWrapper, newThumbsWrapper);
    show();

    if(!this.productGalleryModal) return;

    const galleryWrapperModal = this.productGalleryModal.querySelector('.sn_modal_product_gallery__images .swiper-wrapper');
    const newGalleryWrapperModal = newHtml.querySelector('.sn_modal_product_gallery__images .swiper-wrapper');
    morph(galleryWrapperModal, newGalleryWrapperModal);
  }

  /**
   * Re-renders the details info, such as SKU, wishlist and share
   * @param {Document} newHtml - The new HTML.
   */
  updateDetailsInfo(newHtml) {
    if (!this.isOnPage.product) return;

    // If we are in the product page, get the new gallery, and:
    const detailsInfo = this.closest('.sn_main_product').querySelector('.sn_main_product_details_info');
    const newDetailsInfo = newHtml.querySelector('.sn_main_product .sn_main_product_details_info');
    // console.log({ detailsInfo, newDetailsInfo });

    morph(detailsInfo, newDetailsInfo);
  }

  /**
   * Re-renders the shipment info
   * @param {Document} newHtml - The new HTML.
   */
  updateShippingInfo(newHtml) {
    if (!this.isOnPage.product) return;

    const formInfo = this.closest('.sn_main_product').querySelector('.sn_main_product_buy_form__info');
    const newFormInfo = newHtml.querySelector('.sn_main_product_buy_form__info');
    // console.log({ formInfo, newFormInfo });

    morph(formInfo, newFormInfo, { childrenOnly: false });
  }

  /**
   * Re-renders the shipment info
   * @param {Document} newHtml - The new HTML.
   */
  updateAddOnVariants(newHtml) {
    if (!this.isOnPage.product) return;

    const addOnVariants = this.closest('.sn_main_product').querySelector('.sn_main_product_add_ons__variants');
    if(!addOnVariants) return;

    const newAddOnVariants = newHtml.querySelector('.sn_main_product_add_ons__variants');
    if(!newAddOnVariants) return;

    morph(addOnVariants, newAddOnVariants);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
